import { useState } from "react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { X } from "lucide-react";

const galleryImages = [
  {
    url: "https://images.unsplash.com/photo-1647644520784-2a4bd03a4204?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9vZHklMjBmbG9yYWwlMjBhcnJhbmdlbWVudHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85",
    title: "Midnight Garden",
    category: "Arrangement"
  },
  {
    url: "https://images.unsplash.com/photo-1655788174766-a63b2d4409d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxibGFjayUyMGJhY2tncm91bmQlMjByZWQlMjByb3NlfGVufDB8fHx8MTc3NjI2MjA2N3ww&ixlib=rb-4.1.0&q=85",
    title: "Crimson Solitude",
    category: "Bouquet"
  },
  {
    url: "https://images.unsplash.com/photo-1760977606412-e357b1e21c60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbWluaW1hbGlzdCUyMGZsb3dlcnxlbnwwfHx8fDE3NzYyNjIwNjd8MA&ixlib=rb-4.1.0&q=85",
    title: "Ethereal White",
    category: "Minimalist"
  },
  {
    url: "https://images.unsplash.com/photo-1647644520784-7b6ad81c9efd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjBmbG9yYWwlMjBhcnJhbmdlbWVudHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85",
    title: "Whispered Petals",
    category: "Arrangement"
  },
  {
    url: "https://images.unsplash.com/photo-1724847764267-12775ec49657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3ZWRkaW5nJTIwZmxvcmFsJTIwZGVjb3J8ZW58MHx8fHwxNzc2MjYyMDM5fDA&ixlib=rb-4.1.0&q=85",
    title: "Wedding Installation",
    category: "Event"
  },
  {
    url: "https://images.unsplash.com/photo-1760373071711-960143464e34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmbG9yaXN0JTIwc2hvcHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85",
    title: "Atelier Study",
    category: "Behind the Scenes"
  }
];

export default function Archive() {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="page-transition pt-[72px]" data-testid="archive-page">
      {/* Header */}
      <section className="section bg-[#0c0c0c] border-b border-[#b8966a]/10">
        <div className="container-custom text-center">
          <p className="overline mb-4">Portfolio</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#f8f5f0] mb-4">
            The Archive
          </h1>
          <p className="font-body text-[#a19d98] max-w-2xl mx-auto">
            A curated collection of our most memorable creations. 
            Each piece tells a story of beauty, emotion, and artistry.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(image)}
                className="group relative aspect-[3/4] overflow-hidden border border-[#b8966a]/10 hover:border-[#b8966a]/40 transition-all"
                data-testid={`archive-image-${index}`}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="overline mb-1">{image.category}</p>
                  <h3 className="font-heading text-lg text-[#f8f5f0]">{image.title}</h3>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-[#0c0c0c] border-[#b8966a]/20 p-0">
          <DialogClose className="absolute right-4 top-4 z-10 w-10 h-10 flex items-center justify-center bg-[#0c0c0c]/80 text-[#f8f5f0] hover:text-[#b8966a] transition-colors">
            <X size={20} />
          </DialogClose>
          {selectedImage && (
            <div>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="p-6">
                <p className="overline mb-1">{selectedImage.category}</p>
                <h3 className="font-heading text-xl text-[#f8f5f0]">{selectedImage.title}</h3>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
